import React, { useEffect, useState } from 'react';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import { setFormDetailsQuery } from '../../lib/Queries';
import EditableQuestion from './EditableQuestion.jsx';
import EditQuestionForm from './EditQuestionForm';
import {
    PlusIcon,
    ArrowDownIcon,
    ArrowUpIcon,
    TrashIcon,
} from '@heroicons/react/24/outline/index.js';
import SectionConditional from './SectionConditional.jsx';
import DotDotDotMenu from '../DotDotDotMenu';
import * as Toast from '../../lib/Toast.js';
import CopyIcon from '../icons/CopyIcon.jsx';

const Section = ({
    section,
    hasQuestions,
    children,
    onAddQuestion,
    idx,
    onMoveUp,
    onMoveDown,
                     showAddTopOnly,
    isAddingQuestion,
}) => {
    const [conditional, setConditional] = useState(section.conditional);

    const { isSaving: isDeleting, save: doDelete } = useBusinessPostApi(
        `/forms/${section.form_id}/sections/${section.id}/delete`,
    );
    const { isSaving: isDuplicating, save: saveDuplicate } = useBusinessPostApi(
        `/forms/${section.form_id}/sections/${section.id}/duplicate`,
    );
    const { isSaving, save } = useBusinessPostApi(
        `/forms/${section.form_id}/sections/${section.id}`,
    );

    const isThankYou = !!section.settings?.thankyou;
    const isPayment = !!section.settings?.payment;
    const isSpecial = !!section.is_special;

    function onDeleteClick(ev) {
        if (typeof ev?.preventDefault === 'function') ev.preventDefault();

        if (
            confirm(
                `Are you sure you want to delete this section?${hasQuestions ? '\nThis will delete all the questions in the section and cannot be undone.' : ''}`,
            )
        ) {
            doDelete({}).then(result => {
                setFormDetailsQuery(section.form_id, result);
            });
        }
    }

    function doDuplicate() {
        saveDuplicate({}).then(result => {
            setFormDetailsQuery(section.form_id, result);
            Toast.success(
                `Duplicated section${!!section.conditional ? '. The duplicated section has the conditional of where to go after the section removed.' : ''}`,
            );
            setTimeout(() => {
                // Scroll to the new section...
                const el = document.getElementsByClassName(
                    `section-${result.newSection?.id}`,
                )[0];
                if (el) {
                    el.scrollIntoView({
                        block: 'start',
                        inline: 'nearest',
                        behavior: 'smooth',
                    });
                }
            }, 50);
        });
    }

    useEffect(() => {
        setConditional(section.conditional);
    }, [JSON.stringify(section.conditional)]);

    function updateConditional(newValue) {
        setConditional(newValue);
        save({
            conditional: newValue,
        }).then(result => {
            setFormDetailsQuery(section.form_id, result);
        });
    }

    const moveOptions = [];
    if (onMoveUp) {
        moveOptions.push({
            name: 'Move Up',
            icon: <ArrowUpIcon />,
            onClick: onMoveUp,
        });
    }
    if (onMoveDown) {
        moveOptions.push({
            name: 'Move Down',
            icon: <ArrowDownIcon />,
            onClick: onMoveDown,
        });
    }
    moveOptions.push({
        name: isDuplicating ? 'Duplicating...' : 'Duplicate',
        icon: <CopyIcon />,
        onClick: doDuplicate,
    });
    moveOptions.push({
        name: isDeleting ? 'Deleting...' : 'Delete',
        isDelete: true,
        onClick: onDeleteClick,
    });

    return (
        <div className={`mb-4 section-${section.id}`}>
            {idx !== false && (
                <div className="flex items-center justify-between">
                    <div className="panel-label">Section {idx + 1}</div>
                    {moveOptions.length > 0 && (
                        <DotDotDotMenu options={moveOptions} />
                    )}
                </div>
            )}

            <div
                className={`bg-white rounded-lg p-4 pt-0 mb-1 relative ${!hasQuestions ? 'pt-4' : ''}`}
            >
                {!!showAddTopOnly && (
                    <div>
                        <div className="flex justify-center pt-4 mb-4">
                            <button
                                className="btn-light-pill flex items-center text-sm"
                                onClick={() => onAddQuestion()}
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                {isAddingQuestion ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                    </div>
                )}

                {children}
                {!hasQuestions && !isSpecial && (
                    <button
                        className="absolute right-4 text-red-600 hover:text-red-700"
                        onClick={onDeleteClick}
                    >
                        {!isDeleting && <TrashIcon className=" w-5 h-5 " />}
                        {isDeleting && <>Deleting...</>}
                    </button>
                )}
                {!showAddTopOnly && (
                    <div>
                        <div className="flex justify-center pt-4">
                            <button
                                className="btn-light-pill flex items-center text-sm"
                                onClick={() => onAddQuestion()}
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Add
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {!isSpecial && (
                <SectionConditional
                    section={section}
                    conditional={conditional}
                    setConditional={updateConditional}
                />
            )}
        </div>
    );
};

export default Section;
